import { parseRelinkedServiceField } from '@/lib/relinkedServiceChange'
import type { FollowUpThread } from '@/components/CustomerConversation'
import type {
  CustomerHistoryEvent,
  HistoryLink,
} from '@/components/CustomerHistory'
import type { CalloutNotesSectionNote } from '@/components/CalloutNotesSection'
import type { FollowUp, FollowUpEventType } from '@/db/domain/followUps'
import type { CustomerNote } from '@/db/domain/models'
import { formatServiceStatus } from '@/help/serviceStatus'
import type { Callout, CustomerRevision, Job, Note, StatusChangeLog } from '@/lib/schemas/db'
import { buildCustomerNoteFeed } from '@/screens/serviceFeed'

/**
 * Builders behind the customer's two time-based views.
 *
 * The conversation tab groups by thread and orders by recency; the History tab
 * flattens everything back into strict chronology. Both are derived here, from
 * the same domain records, so the two views can never disagree about what
 * happened — only about how to arrange it.
 */

type GetName = (userId: string) => string

function latest(...timestamps: Array<string | undefined>): string {
  return timestamps.filter(Boolean).sort().pop() ?? ''
}

export function buildFollowUpThreads(
  followUps: FollowUp[],
  customerNotes: CustomerNote[],
  getName: GetName,
): FollowUpThread[] {
  return followUps.map((followUp) => {
    const threadNotes = customerNotes
      .filter((note) => note.followUpId === followUp.id)
      .sort((a, b) => a.createdAt.localeCompare(b.createdAt))
    // Live answers gate done/close and feed the action dialog; the feed keeps
    // deleted ones too, rendered as tombstones by the notes section.
    const answers = threadNotes
      .filter((note) => !note.deletedAt)
      .map((note) => ({
        id: note.id,
        text: note.text ?? note.caption ?? '(photo note)',
        authorName: getName(note.authorId),
        createdAt: note.createdAt,
      }))
      .reverse()

    return {
      id: followUp.id,
      title: followUp.title,
      details: followUp.details,
      status: followUp.status,
      dueAt: followUp.dueAt,
      createdAt: followUp.createdAt,
      schedule: followUp.schedule,
      scheduleIndex: followUp.scheduleIndex,
      feed: buildCustomerNoteFeed(threadNotes, getName),
      answers,
      assigneeName: followUp.assigneeId
        ? getName(followUp.assigneeId)
        : undefined,
      lastActivityAt: latest(
        followUp.updatedAt ?? followUp.createdAt,
        threadNotes[threadNotes.length - 1]?.createdAt,
      ),
    }
  })
}

export function buildGeneralNotes(
  customerNotes: CustomerNote[],
  getName: GetName,
): CalloutNotesSectionNote[] {
  return buildCustomerNoteFeed(
    customerNotes.filter((note) => !note.followUpId),
    getName,
  )
}

export interface ServiceForThread {
  id: string
  routeId: string
  serviceType: 'Callout' | 'Session' | 'Job'
  title: string
  status: Callout['status'] | Job['status']
  bookedAt: string
}

const followUpEventVerbs: Record<FollowUpEventType, string> = {
  created: 'raised',
  done: 'marked done',
  snoozed: 'snoozed',
  rescheduled: 'rescheduled',
  closed: 'closed',
  reopened: 'reopened',
  assigned: 'assigned',
}

function snippet(text: string | undefined, max = 120): string {
  const trimmed = (text ?? '').trim()
  if (!trimmed) return ''
  return trimmed.length > max ? `${trimmed.slice(0, max - 1)}…` : trimmed
}

/** An archived (Invoiced/Cancelled) service, known only from the archive rows. */
export interface ArchivedServiceForHistory {
  routeId: string
  serviceType: 'Callout' | 'Session' | 'Job'
  title: string
  status: string
  bookedAt: string
}

export function buildHistoryEvents(input: {
  customerNotes: CustomerNote[]
  followUps: FollowUp[]
  services: ServiceForThread[]
  archivedServices?: ArchivedServiceForHistory[]
  notesByServiceId: Map<string, Note[]>
  statusChangesByServiceId: Map<string, StatusChangeLog[]>
  customerRevisions?: CustomerRevision[]
  getName: GetName
}): CustomerHistoryEvent[] {
  const {
    customerNotes,
    followUps,
    services,
    archivedServices = [],
    notesByServiceId,
    statusChangesByServiceId,
    customerRevisions = [],
    getName,
  } = input

  const events: CustomerHistoryEvent[] = []

  // Details edits are add-only (customer_revisions); each one is a batch of
  // field changes that also reads back what it replaced.
  for (const revision of customerRevisions) {
    // Services moved in by an archive get a row each — clickable, with the
    // invoice-snapshot note — instead of drowning in the details summary.
    for (const change of revision.changes) {
      const relinked = parseRelinkedServiceField(change.field)
      if (!relinked) continue
      events.push({
        id: `custrev-${revision.id}-${change.field}`,
        at: revision.createdAt,
        kind: 'service',
        label: relinked.serviceType,
        description: `${relinked.ref} relinked ${relinked.direction} ${change.before} — ${change.after}`,
        actorName: getName(revision.authorId),
        link: { kind: 'service', serviceType: relinked.serviceType, routeId: relinked.ref },
      })
    }
    const summary = revision.changes
      .filter((change) => !parseRelinkedServiceField(change.field))
      .map(
        (change) =>
          `${change.label}: ${change.before === '' ? '(empty)' : change.before} → ${change.after === '' ? '(cleared)' : change.after}`,
      )
      .join(', ')
    events.push({
      id: `custrev-${revision.id}`,
      at: revision.createdAt,
      kind: 'details_changed',
      label: 'Details',
      description: snippet(summary, 200) || 'Details updated',
      actorName: getName(revision.authorId),
    })
  }
  const followUpTitleById = new Map(
    followUps.map((followUp) => [followUp.id, followUp.title]),
  )

  for (const note of customerNotes) {
    const answering = note.followUpId
      ? ` — on “${followUpTitleById.get(note.followUpId) ?? 'a follow-up'}”`
      : ''
    const threadLink: HistoryLink = {
      kind: 'thread',
      threadKey: note.followUpId ? `fu:${note.followUpId}` : 'general',
    }
    events.push({
      id: `note-${note.id}`,
      at: note.createdAt,
      kind: 'note',
      label: 'Note',
      description: `“${snippet(note.text ?? note.caption) || '(photo)'}”${answering}`,
      actorName: getName(note.authorId),
      link: threadLink,
    })
    for (const [index, revision] of (note.revisions ?? []).entries()) {
      events.push({
        id: `note-${note.id}-rev-${index}`,
        at: revision.at,
        kind: 'note_edited',
        label: 'Note edited',
        description: `was “${snippet(revision.text ?? revision.caption) || '(photo)'}”`,
        actorName: getName(revision.authorId),
        link: threadLink,
      })
    }
    if (note.deletedAt) {
      events.push({
        id: `note-${note.id}-del`,
        at: note.deletedAt,
        kind: 'note_deleted',
        label: 'Note deleted',
        description: `“${snippet(note.text ?? note.caption) || '(photo)'}”`,
        actorName: note.deletedBy ? getName(note.deletedBy) : undefined,
        link: threadLink,
      })
    }
  }

  for (const followUp of followUps) {
    for (const [index, event] of (followUp.history ?? []).entries()) {
      events.push({
        id: `fu-${followUp.id}-${index}`,
        at: event.at,
        kind: 'follow_up',
        label: 'Follow-up',
        description: `${followUpEventVerbs[event.type] ?? event.type} — “${followUp.title}”`,
        actorName: getName(event.by),
        link: { kind: 'thread', threadKey: `fu:${followUp.id}` },
      })
    }
  }

  for (const service of services) {
    const serviceLink: HistoryLink = {
      kind: 'service',
      serviceType: service.serviceType,
      routeId: service.routeId,
    }
    events.push({
      id: `svc-${service.serviceType}-${service.id}`,
      at: service.bookedAt,
      kind: 'service',
      label: service.serviceType,
      description: `${service.routeId} booked — ${snippet(service.title) || 'no summary'}`,
      link: serviceLink,
    })
    for (const note of notesByServiceId.get(service.id) ?? []) {
      events.push({
        id: `svcnote-${note.id}`,
        at: note.createdAt,
        kind: 'note',
        label: 'Work note',
        description: `on ${service.routeId}: “${snippet(note.text ?? note.caption) || '(photo)'}”`,
        actorName: getName(note.authorId),
        link: serviceLink,
      })
    }
    for (const change of statusChangesByServiceId.get(service.id) ?? []) {
      events.push({
        id: `status-${change.id}`,
        at: change.createdAt,
        kind: 'status',
        label: 'Status',
        description: `${service.routeId} → ${formatServiceStatus(change.newStatus)}${change.reason ? ` — ${snippet(change.reason, 80)}` : ''}`,
        actorName: getName(change.authorId),
        link: serviceLink,
      })
    }
  }

  // Invoiced/Cancelled services live outside the sync; the archive rows give
  // them one booked-entry each so the old work stays findable — and openable.
  for (const archived of archivedServices) {
    events.push({
      id: `arch-${archived.serviceType}-${archived.routeId}`,
      at: archived.bookedAt,
      kind: 'service',
      label: archived.serviceType,
      description: `${archived.routeId} booked — ${snippet(archived.title) || 'no summary'} (now ${formatServiceStatus(archived.status as Callout['status'])})`,
      link: {
        kind: 'service',
        serviceType: archived.serviceType,
        routeId: archived.routeId,
      },
    })
  }

  return events
}
